import { useCallback, useEffect, useMemo } from "react";
// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore } from "../store";
import useGlobalContext from "./useGlobalContext";
import { flightsLocalStore } from "../utils/local-store";
import useClientMetrics from "./useClientMetrics";
import {
  UIFlightData,
  UILuggageCabinAllowance,
  UILuggageCheckedAllowance,
  UILuggagePersonalItemAllowance,
  UIProductCabinBaggagePerTraveller,
  UIProductCheckedInBaggage
} from "@flights/types";

export default function useReportBaggageQuality(token: string) {
  const LOCALSTORAGEKEY = "fd_v_sr_baggage_info_quality_check";
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const { features } = useGlobalContext();
  const { trackClientMetric } = useClientMetrics();

  const flightSR = useMemo(() => {
    return store?.searchResults?.flights?.find((flight) => flight?.token == token);
  }, [token, store]);

  const isReported = useCallback((token: string) => {
    if (!flightsLocalStore) return false;
    const data: string[] = flightsLocalStore.get(LOCALSTORAGEKEY) || [];
    return data.some((k) => k === token);
  }, []);

  const setIsReported = useCallback((token: string) => {
    if (!flightsLocalStore) return;
    const data = flightsLocalStore.get(LOCALSTORAGEKEY) || [];
    data.push(token);
    flightsLocalStore.set(LOCALSTORAGEKEY, data);
  }, []);

  const reportPersonalItem = useCallback(
    (srItem?: UILuggagePersonalItemAllowance, fdItem?: UILuggagePersonalItemAllowance) => {
      if (srItem !== undefined && fdItem !== undefined) {
        trackClientMetric("fd_v_sr_baggage_info_personal_item_available_match", 1);
      }
      if (srItem !== undefined && fdItem === undefined) {
        trackClientMetric("fd_v_sr_baggage_info_personal_item_available_mismatch", 1);
      }
      if (srItem === undefined && fdItem === undefined) {
        trackClientMetric("fd_v_sr_baggage_info_personal_item_not_available_match", 1);
      }
      if (srItem === undefined && fdItem !== undefined) {
        trackClientMetric("fd_v_sr_baggage_info_personal_item_not_available_mismatch", 1);
      }
    },
    [trackClientMetric]
  );

  const reportCabinBag = useCallback(
    (srItem?: UILuggageCabinAllowance, fdItem?: UILuggageCabinAllowance) => {
      if (srItem !== undefined && fdItem !== undefined) {
        trackClientMetric("fd_v_sr_baggage_info_cabin_available_match", 1);
      }
      if (srItem !== undefined && fdItem === undefined) {
        trackClientMetric("fd_v_sr_baggage_info_cabin_available_mismatch", 1);
      }
      if (srItem === undefined && fdItem === undefined) {
        trackClientMetric("fd_v_sr_baggage_info_cabin_not_available_match", 1);
      }
      if (srItem === undefined && fdItem !== undefined) {
        trackClientMetric("fd_v_sr_baggage_info_cabin_not_available_mismatch", 1);
      }
      if (!!srItem?.maxWeightPerPiece) {
        const isEqualMaxWeight = srItem.maxWeightPerPiece === fdItem?.maxWeightPerPiece;
        if (isEqualMaxWeight) trackClientMetric("fd_v_sr_baggage_info_cabin_max_weight_match", 1);
        if (!isEqualMaxWeight) trackClientMetric("fd_v_sr_baggage_info_cabin_max_weight_mismatch", 1);
      }
      if (!!srItem?.maxWeightPerPiece) {
        const isEqualMaxPiece = srItem.maxPiece === fdItem?.maxPiece;
        if (isEqualMaxPiece) trackClientMetric("fd_v_sr_baggage_info_cabin_max_piece_match", 1);
        if (!isEqualMaxPiece) trackClientMetric("fd_v_sr_baggage_info_cabin_max_piece_mismatch", 1);
      }
      const srSizeRestrictions = srItem?.sizeRestrictions;
      const fdSizeRestrictions = fdItem?.sizeRestrictions;
      if (!!srSizeRestrictions?.maxHeight && !!srSizeRestrictions?.maxLength && !!srSizeRestrictions?.maxWidth) {
        const isEqualDimension =
          srSizeRestrictions.maxHeight === fdSizeRestrictions?.maxHeight &&
          srSizeRestrictions.maxLength === fdSizeRestrictions?.maxLength &&
          srSizeRestrictions.maxWidth === fdSizeRestrictions?.maxWidth;
        if (isEqualDimension) trackClientMetric("fd_v_sr_baggage_info_cabin_dimensions_match", 1);
        if (!isEqualDimension) trackClientMetric("fd_v_sr_baggage_info_cabin_dimensions_mismatch", 1);
      }
    },
    [trackClientMetric]
  );

  const reportCheckedBag = useCallback(
    (srItem?: UILuggageCheckedAllowance, fdItem?: UILuggageCheckedAllowance) => {
      if (srItem !== undefined && fdItem !== undefined) {
        trackClientMetric("fd_v_sr_baggage_info_checked_available_match", 1);
      }
      if (srItem !== undefined && fdItem === undefined) {
        trackClientMetric("fd_v_sr_baggage_info_checked_available_mismatch", 1);
      }
      if (srItem === undefined && fdItem === undefined) {
        trackClientMetric("fd_v_sr_baggage_info_checked_not_available_match", 1);
      }
      if (srItem === undefined && fdItem !== undefined) {
        trackClientMetric("fd_v_sr_baggage_info_checked_not_available_mismatch", 1);
      }
      if (!!srItem?.maxWeightPerPiece) {
        const isEqualMaxWeight = srItem.maxWeightPerPiece === fdItem?.maxWeightPerPiece;
        if (isEqualMaxWeight) trackClientMetric("fd_v_sr_baggage_info_checked_max_weight_match", 1);
        if (!isEqualMaxWeight) trackClientMetric("fd_v_sr_baggage_info_checked_max_weight_mismatch", 1);
      }
    },
    [trackClientMetric]
  );

  const reportCabinBagPerTraveller = useCallback(
    (srItem?: UIProductCabinBaggagePerTraveller, fdItem?: UIProductCabinBaggagePerTraveller) => {
      if (srItem !== undefined && fdItem !== undefined) {
        trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_available_match", 1);
      }
      if (srItem !== undefined && fdItem === undefined) {
        trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_available_mismatch", 1);
      }
      if (srItem === undefined && fdItem === undefined) {
        trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_not_available_match", 1);
      }
      if (srItem === undefined && fdItem !== undefined) {
        trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_not_available_mismatch", 1);
      }
      if (!!srItem?.luggageAllowance?.maxWeightPerPiece) {
        const isEqualMaxWeight =
          srItem.luggageAllowance.maxWeightPerPiece === fdItem?.luggageAllowance?.maxWeightPerPiece;
        if (isEqualMaxWeight) trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_max_weight_match", 1);
        if (!isEqualMaxWeight) trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_max_weight_mismatch", 1);
      }
      if (!!srItem?.luggageAllowance?.maxPiece) {
        const isEqualMaxPiece = srItem.luggageAllowance.maxPiece === fdItem?.luggageAllowance?.maxPiece;
        if (isEqualMaxPiece) trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_max_piece_match", 1);
        if (!isEqualMaxPiece) trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_max_piece_mismatch", 1);
      }
      if (srItem?.luggageAllowance?.luggageType === "HAND") {
        const srSizeRestrictions = srItem?.luggageAllowance?.sizeRestrictions;
        // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
        const fdSizeRestrictions = fdItem?.luggageAllowance?.["sizeRestrictions"];
        if (!!srSizeRestrictions?.maxHeight && !!srSizeRestrictions?.maxLength && !!srSizeRestrictions?.maxWidth) {
          const isEqualDimension =
            srSizeRestrictions.maxHeight === fdSizeRestrictions?.maxHeight &&
            srSizeRestrictions.maxLength === fdSizeRestrictions?.maxLength &&
            srSizeRestrictions.maxWidth === fdSizeRestrictions?.maxWidth;
          if (isEqualDimension) trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_dimensions_match", 1);
          if (!isEqualDimension) trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_dimensions_match", 1);
        }
      }
    },
    [trackClientMetric]
  );

  const reportCheckedInBag = useCallback(
    (srItem?: UIProductCheckedInBaggage, fdItem?: UIProductCheckedInBaggage) => {
      if (srItem?.options !== undefined && fdItem?.options !== undefined) {
        trackClientMetric("fd_v_sr_baggage_info_checked_in_available_match", 1);
      }
      if (srItem?.options !== undefined && fdItem?.options === undefined) {
        trackClientMetric("fd_v_sr_baggage_info_checked_in_available_mismatch", 1);
      }
      if (srItem?.options === undefined && fdItem?.options === undefined) {
        trackClientMetric("fd_v_sr_baggage_info_checked_in_not_available_match", 1);
      }
      if (srItem?.options === undefined && fdItem?.options !== undefined) {
        trackClientMetric("fd_v_sr_baggage_info_checked_in_not_available_mismatch", 1);
      }
      srItem?.options?.forEach((srOption, idx) => {
        const fdOption = fdItem?.options?.[idx];
        if (!!srOption?.luggageAllowance?.maxWeightPerPiece) {
          const isEqualMaxWeight =
            srOption.luggageAllowance.maxWeightPerPiece === fdOption?.luggageAllowance?.maxWeightPerPiece;
          if (isEqualMaxWeight) trackClientMetric("fd_v_sr_baggage_info_checked_in_max_weight_match", 1);
          if (!isEqualMaxWeight) trackClientMetric("fd_v_sr_baggage_info_checked_in_max_weight_mismatch", 1);

          const isEqualMaxPiece = srOption.luggageAllowance.maxPiece === fdOption?.luggageAllowance?.maxPiece;
          if (isEqualMaxPiece) trackClientMetric("fd_v_sr_baggage_info_checked_in_max_piece_match", 1);
          if (!isEqualMaxPiece) trackClientMetric("fd_v_sr_baggage_info_checked_in_max_piece_mismatch", 1);
        }
      });
    },
    [trackClientMetric]
  );

  const reportBaggageQuality = useCallback(
    (flightSR: UIFlightData, flightFD: UIFlightData) => {
      flightFD?.includedProducts?.segments.forEach((segmentFD, idx) => {
        const segmentSR = flightSR.includedProducts?.segments[idx];
        const checkedInFD = segmentFD.find((segment) => segment.luggageType === "CHECKED_IN") as
          | UILuggageCheckedAllowance
          | undefined;
        const checkedInSR = segmentSR?.find((segment) => segment.luggageType === "CHECKED_IN") as
          | UILuggageCheckedAllowance
          | undefined;
        reportCheckedBag(checkedInSR, checkedInFD);
        const handFD = segmentFD.find((segment) => segment.luggageType === "HAND") as
          | UILuggageCabinAllowance
          | undefined;
        const handSR = segmentSR?.find((segment) => segment.luggageType === "HAND") as
          | UILuggageCabinAllowance
          | undefined;
        reportCabinBag(handSR, handFD);
        const personalItemFD = segmentFD.find((segment) => segment.luggageType === "PERSONAL_ITEM") as
          | UILuggagePersonalItemAllowance
          | undefined;
        const personalItemSR = segmentSR?.find((segment) => segment.luggageType === "PERSONAL_ITEM") as
          | UILuggagePersonalItemAllowance
          | undefined;
        reportPersonalItem(personalItemSR, personalItemFD);
      });

      const cabinBaggagePerTravellerFD = flightFD?.ancillaries?.cabinBaggagePerTraveller;
      const cabinBaggagePerTravellerSR = flightSR?.ancillaries?.cabinBaggagePerTraveller;
      reportCabinBagPerTraveller(cabinBaggagePerTravellerSR, cabinBaggagePerTravellerFD);

      const checkedInBaggageFD = flightFD?.ancillaries?.checkedInBaggage;
      const checkedInBaggageSR = flightSR?.ancillaries?.checkedInBaggage;
      reportCheckedInBag(checkedInBaggageSR, checkedInBaggageFD);

      setIsReported(token);
    },
    [
      token,
      setIsReported,
      reportPersonalItem,
      reportCabinBag,
      reportCheckedBag,
      reportCabinBagPerTraveller,
      reportCheckedInBag
    ]
  );

  useEffect(() => {
    if (!features.FLIGHTS_WEB_REPORT_BAGGAGE_QUALITY) return;
    if (!token || isReported(token)) return;
    const flightFD = store.flightDetails.flight;
    if (flightSR === undefined || flightFD === undefined) return;
    reportBaggageQuality(flightSR, flightFD);
  }, [token, flightSR, store, features, isReported, reportBaggageQuality]);

  return { reportBaggageQuality };
}
